import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { MdOutlineCancel } from 'react-icons/md';
import {
  FaServer,
  FaTicketAlt,
  FaHeadset,
  FaFileInvoiceDollar,
  FaBuilding,
  FaUsers,
  FaUserShield,
  FaHospital,
  FaCubes,
  FaShieldAlt,
} from 'react-icons/fa';
import { useStateContext } from '../../contexts/ContextProvider';
import TMLogo from '../../components/TMLogo';
import TMLogoIcon from '../../components/TMLogoIcon';

type AdminMenuItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: AdminMenuItem[];
};

const menu: AdminMenuItem[] = [
  { label: 'Servers', path: '/admin/servers', icon: <FaServer /> },
  { label: 'Tickets', path: '/admin/tickets', icon: <FaTicketAlt /> },
  { label: 'Remote Support', path: '/admin/remote-support', icon: <FaHeadset /> },
  { label: 'Subscriptions', path: '/admin/subscriptions', icon: <FaFileInvoiceDollar /> },
  {
    label: 'Security',
    path: '/admin/security',
    icon: <FaShieldAlt />,
    children: [
      { label: 'Roles', path: '/admin/roles', icon: <FaUserShield /> },
      { label: 'Users', path: '/admin/clients/users', icon: <FaUsers /> },
    ],
  },
  { label: 'Clients', path: '/admin/clients', icon: <FaBuilding /> },
  { label: 'Facilities', path: '/admin/clients/facilities', icon: <FaHospital /> },
  { label: 'Modules', path: '/admin/clients/facilities/modules', icon: <FaCubes /> },
];

const AdminSidebar: React.FC = () => {
  const { sidebarState, screenSize, setSidebarState } = useStateContext();

  const handleCloseSideBar = () => {
    if (sidebarState !== 'hidden' && screenSize && screenSize <= 900) {
      setSidebarState('hidden');
    }
  };

  const renderMenuItem = (item: AdminMenuItem) => {
    if (item.children) {
      return (
        <li key={item.path}>
          <details open>
            <summary>
              <span className="flex items-center gap-3">
                <span className="text-lg">{item.icon}</span>
                {item.label}
              </span>
            </summary>
            <ul>
              {item.children.map((child) => renderMenuItem(child))}
            </ul>
          </details>
        </li>
      );
    }

    return (
      <li key={item.path}>
        <NavLink to={item.path} end onClick={handleCloseSideBar}>
          <span className="flex items-center gap-3">
            <span className="text-lg">{item.icon}</span>
            {item.label}
          </span>
        </NavLink>
      </li>
    );
  };

  const flattenMenuItems = (items: AdminMenuItem[]): AdminMenuItem[] => {
    return items.flatMap((item) => (item.children ? flattenMenuItems(item.children) : [item]));
  };

  const renderCollapsedMenuItem = (item: AdminMenuItem) => (
    <li key={item.path}>
      <TooltipComponent content={item.label} position="RightCenter">
        <NavLink to={item.path} end onClick={handleCloseSideBar} className="flex justify-center">
          <span className="text-xl">{item.icon}</span>
        </NavLink>
      </TooltipComponent>
    </li>
  );

  return (
    <div className="h-screen flex flex-col bg-base-100 overflow-hidden">
      <div className="pb-3 border-b border-base-300">
        <div className="flex justify-between items-center p-3">
          <Link to="/admin" onClick={handleCloseSideBar} className="flex w-full">
            {sidebarState === 'full' ? (
              <div className="w-full flex flex-col items-center justify-center">
                <TMLogo className="h-28 w-auto tm-logo-tone" />
                <h2 className="text-sm uppercase tracking-wide font-bold text-base-content opacity-70 -mt-2">
                  Administration
                </h2>
              </div>
            ) : (
              <TooltipComponent content="Administration" position="RightCenter">
                <div className="w-full flex items-center justify-center">
                  <TMLogoIcon className="h-10 w-10 tm-logo-tone" />
                </div>
              </TooltipComponent>
            )}
          </Link>

          {screenSize && screenSize <= 900 && (
            <TooltipComponent content="Close" position="BottomCenter">
              <button
                type="button"
                onClick={() => setSidebarState('hidden')}
                className="text-xl text-base-content rounded-full p-3 hover:bg-base-200"
              >
                <MdOutlineCancel />
              </button>
            </TooltipComponent>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <ul className={`menu ${sidebarState === 'collapsed' ? 'menu-compact' : 'menu-sm'}`}>
          {sidebarState === 'collapsed'
            ? flattenMenuItems(menu).map((item) => renderCollapsedMenuItem(item))
            : menu.map((item) => renderMenuItem(item))}
        </ul>
      </div>
    </div>
  );
};

export default AdminSidebar;
